import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Calendar, CheckCircle, XCircle, AlertTriangle, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_LABELS = { pto: 'PTO', sick: 'Sick', unpaid: 'Unpaid', bereavement: 'Bereavement', other: 'Other' };
const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const dayLabel = (iso) => {
  try { const d = new Date(iso + 'T12:00:00'); return `${WEEKDAY[d.getDay()]} ${iso}`; } catch { return iso; }
};

const AdminTimeOff = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [requests, setRequests] = useState([]);
  const [review, setReview] = useState(null);        // approval-preview payload
  const [reviewFor, setReviewFor] = useState(null);  // request row being reviewed
  const [dayHours, setDayHours] = useState({});
  const [reviewType, setReviewType] = useState('pto');
  const [override, setOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncReport, setSyncReport] = useState(null);
  const [ptoBalOverride, setPtoBalOverride] = useState(false);
  const [ptoOverrideReason, setPtoOverrideReason] = useState('');

  useEffect(() => { refresh(); }, []);

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/timeoff`, { withCredentials: true });
      setRequests(data);
    } catch (err) { console.error(err); }
  };

  const openReview = async (r) => {
    setReviewFor(r);
    setReview(null);
    setOverride(false);
    setOverrideReason('');
    try {
      const { data } = await axios.get(`${API}/timeoff/${r.id}/approval-preview`, { withCredentials: true });
      setReview(data);
      setDayHours({ ...(data.day_hours || {}) });
      setReviewType(data.time_off_type || 'pto');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load request');
      setReviewFor(null);
    }
  };

  const totalPaid = Object.values(dayHours).reduce((a, b) => a + (Number(b) || 0), 0);

  const submitApproval = async () => {
    const hasConflict = (review?.conflicts || []).length > 0;
    if (hasConflict && !override) {
      toast.error('Resolve the conflict, or enable Super Admin override to proceed.');
      return;
    }
    if (hasConflict && override && !overrideReason.trim()) {
      toast.error('An override reason is required.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        status: 'approved',
        time_off_type: reviewType,
        day_hours: Object.fromEntries(Object.entries(dayHours).map(([k, v]) => [k, Number(v) || 0])),
      };
      if (hasConflict && override) { body.override = true; body.override_reason = overrideReason.trim(); }
      if (ptoBalOverride) { body.pto_over_balance_override = true; body.pto_override_reason = ptoOverrideReason.trim(); }
      const { data } = await axios.put(`${API}/timeoff/${reviewFor.id}`, body, { withCredentials: true });
      toast.success(`Approved — ${data.total_paid_hours?.toFixed(2)} paid hours, ${data.block_ids?.length || 0} day(s) blocked`);
      setReviewFor(null); setReview(null); setPtoBalOverride(false); setPtoOverrideReason('');
      refresh();
    } catch (e) {
      const det = e.response?.data?.detail;
      if (det && typeof det === 'object' && det.code === 'TIME_OFF_CONFLICT') {
        toast.error(det.message);
        // refresh conflicts in-dialog
        setReview((prev) => prev ? { ...prev, conflicts: det.conflicts || [] } : prev);
      } else if (det && typeof det === 'object' && det.code === 'PTO_INSUFFICIENT') {
        toast.error(det.message);
      } else if (det && typeof det === 'object' && det.code === 'PTO_NOT_ELIGIBLE') {
        toast.error(det.message);
      } else {
        toast.error(typeof det === 'string' ? det : 'Approval failed');
      }
    }
    setSaving(false);
  };

  const submitReject = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/timeoff/${rejectFor.id}`, { status: 'rejected', decline_reason: rejectReason.trim() }, { withCredentials: true });
      toast.success('Request rejected');
      setRejectFor(null); setRejectReason('');
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const cancelApproved = async (r) => {
    if (!window.confirm('Cancel this approved time off and remove its schedule blocks?')) return;
    try {
      const { data } = await axios.post(`${API}/timeoff/${r.id}/cancel`, {}, { withCredentials: true });
      toast.success(`Time off canceled — ${data.blocks_removed} block(s) removed`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const syncBlocks = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/admin/timeoff/reconcile-blocks`, {}, { withCredentials: true });
      setSyncReport(data);
      toast.success(`Sync complete — ${data.blocks_created} created, ${data.blocks_removed} removed`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    }
    setSyncing(false);
  };

  const colorOf = (s) => ({
    approved: 'text-green-500 bg-green-500/10',
    rejected: 'text-red-500 bg-red-500/10',
    canceled: 'text-[#A3A3A3] bg-[#262626]',
    pending: 'text-[#D4AF37] bg-[#D4AF37]/10',
  })[s] || 'text-[#A3A3A3] bg-[#262626]';

  return (
    <div data-testid="admin-timeoff-page" className="space-y-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2">Time Off Requests</h2>
          <p className="text-[#A3A3A3]">Review, set paid hours per day, and approve. Approved time off blocks the schedule automatically.</p>
        </div>
        {isSuperAdmin && (
          <Button
            data-testid="sync-timeoff-blocks"
            onClick={syncBlocks}
            disabled={syncing}
            variant="outline"
            className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10"
            title="Backfill / repair schedule blocks for every approved request. Safe & idempotent."
          >
            <Calendar className="w-4 h-4 mr-2" />
            {syncing ? 'Syncing…' : 'Sync Approved Time Off'}
          </Button>
        )}
      </div>

      <div className="grid gap-3">
        {requests.length === 0 ? (
          <div className="bg-[#141414] border border-[#262626] rounded-md p-8 text-center text-[#A3A3A3]">No requests</div>
        ) : requests.map((r) => (
          <div key={r.id} data-testid={`timeoff-row-${r.id}`} className="bg-[#141414] border border-[#262626] rounded-md p-4 flex items-center justify-between">
            <div className="flex items-start gap-3 flex-1">
              <Calendar className="w-5 h-5 text-[#D4AF37] mt-1" />
              <div>
                <p className="text-white font-medium flex items-center gap-2">
                  {r.user_name}
                  <span className="px-2 py-0.5 rounded text-[11px] bg-[#262626] text-[#A3A3A3] uppercase tracking-wider">{TYPE_LABELS[r.time_off_type] || r.time_off_type || 'PTO'}</span>
                  {r.has_conflict && <span className="px-2 py-0.5 rounded text-[11px] bg-orange-500/15 text-orange-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Override</span>}
                </p>
                <p className="text-[#D4AF37] text-sm">{r.start_date} → {r.end_date}</p>
                <p className="text-[#A3A3A3] text-sm">{r.reason}</p>
                {r.status === 'approved' && typeof r.total_paid_hours === 'number' && (
                  <p className="text-green-400 text-xs mt-1">{r.total_paid_hours.toFixed(2)} paid leave hours</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-2 py-1 rounded text-xs ${colorOf(r.status)}`}>{r.status?.toUpperCase()}</span>
              {r.status === 'pending' && (
                <>
                  <Button data-testid={`review-timeoff-${r.id}`} onClick={() => openReview(r)} size="sm" className="bg-green-600 hover:bg-green-700 text-white"><CheckCircle className="w-4 h-4 mr-1" /> Review & Approve</Button>
                  <Button data-testid={`reject-timeoff-${r.id}`} onClick={() => { setRejectFor(r); setRejectReason(''); }} size="sm" variant="outline" className="border-[#7F1D1D] text-red-400 hover:bg-[#7F1D1D] hover:text-white"><XCircle className="w-4 h-4 mr-1" /> Reject</Button>
                </>
              )}
              {r.status === 'approved' && (
                <Button data-testid={`cancel-timeoff-${r.id}`} onClick={() => cancelApproved(r)} size="sm" variant="outline" className="border-[#262626] text-[#A3A3A3] hover:text-white"><Ban className="w-4 h-4 mr-1" /> Cancel</Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Approval dialog — per-day paid hours + conflict handling */}
      <Dialog open={!!reviewFor} onOpenChange={(o) => { if (!o) { setReviewFor(null); setReview(null); } }}>
        <DialogContent data-testid="timeoff-approve-dialog" className="bg-[#141414] border-[#262626] text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Approve Time Off — {reviewFor?.user_name}</DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">Confirm the type and the paid leave hours for each day. Paid leave stays separate from worked hours.</DialogDescription>
          </DialogHeader>
          {!review ? (
            <div className="py-8 text-center text-[#A3A3A3]">Loading…</div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Type</Label>
                <select data-testid="approve-type" value={reviewType} onChange={(e) => setReviewType(e.target.value)} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-2 text-white text-sm mt-1">
                  {(review.types || []).map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>

              {(review.conflicts || []).length > 0 && (
                <div data-testid="approve-conflicts" className="rounded-md border border-orange-700/50 bg-orange-950/20 p-3">
                  <p className="text-orange-300 text-sm font-medium flex items-center gap-2"><AlertTriangle className="w-4 h-4" />Scheduling conflict on {(review.conflicts).length} day(s)</p>
                  <ul className="mt-2 space-y-1 text-xs text-orange-200/90">
                    {review.conflicts.map((c) => (
                      <li key={c.appointment_id}>{c.date} · {c.start_time}–{c.end_time} · {c.service_type}{c.customer_name ? ` (${c.customer_name})` : ''}</li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-[#A3A3A3] mt-2">Reassign, reschedule, remove the employee from the crew, or cancel the job first. Only a Super Admin may override.</p>
                  {review.can_override ? (
                    <div className="mt-3 space-y-2">
                      <label className="flex items-center gap-2 text-sm text-white">
                        <input type="checkbox" data-testid="approve-override" checked={override} onChange={(e) => setOverride(e.target.checked)} className="accent-orange-500" />
                        Super Admin override — approve anyway and flag the conflict
                      </label>
                      {override && (
                        <Textarea data-testid="approve-override-reason" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} rows={2} placeholder="Override reason (required)" className="bg-[#0A0A0A] border-[#262626] text-white" />
                      )}
                    </div>
                  ) : (
                    <p className="text-[11px] text-red-300 mt-2">You are not a Super Admin — this request cannot be approved until the conflict is resolved.</p>
                  )}
                </div>
              )}

              {review.is_pto && review.pto_eligible === false && (
                <div data-testid="approve-eligibility-block" className="rounded-md border border-yellow-700/50 bg-yellow-900/10 p-3 text-sm text-yellow-300">
                  ⏳ This employee is not PTO-eligible until <b>{review.pto_eligibility_date || 'their eligibility date'}</b>. PTO can't be approved before then. (Sick/unpaid leave is unaffected.)
                </div>
              )}

              {review.is_pto && review.pto_summary && (
                <div data-testid="approve-pto-panel" className="rounded-md border border-[#262626] bg-[#0F0F0F] p-3 text-sm">
                  <div className="flex items-center justify-between"><span className="text-[#A3A3A3]">Current Available</span><span className="text-white font-semibold" data-testid="approve-pto-available">{review.pto_summary.current_available.toFixed(2)}h</span></div>
                  <div className="flex items-center justify-between"><span className="text-[#A3A3A3]">Other Pending Requests</span><span className="text-[#D4AF37]">{review.pto_summary.pending_requests.toFixed(2)}h</span></div>
                  <div className="flex items-center justify-between"><span className="text-[#A3A3A3]">This request draws down</span><span className="text-white">{Math.max(0, totalPaid - (review.pto_already_used || 0)).toFixed(2)}h</span></div>
                  {(() => {
                    const draw = Math.max(0, totalPaid - (review.pto_already_used || 0));
                    const after = review.pto_summary.current_available - draw;
                    const exceeds = draw > review.pto_summary.current_available + 1e-9;
                    return (
                      <>
                        <div className="flex items-center justify-between border-t border-[#262626] mt-2 pt-2"><span className="text-[#A3A3A3]">Balance after approval</span><span className={after < 0 ? 'text-red-400 font-bold' : 'text-green-400 font-semibold'} data-testid="approve-pto-after">{after.toFixed(2)}h</span></div>
                        {exceeds && (
                          <>
                            <p data-testid="approve-pto-warning" className="text-red-400 text-xs mt-2 font-medium">⚠ Not enough PTO available. Reduce the approved hours{review.can_balance_override ? ', or use an authorized over-balance override below.' : ', or add a PTO grant first.'}</p>
                            {review.can_balance_override && (
                              <div className="mt-2 rounded-md bg-[#1a0f0f] border border-red-900/40 p-2">
                                <label className="flex items-center gap-2 text-red-300 text-xs cursor-pointer" data-testid="approve-balance-override-toggle">
                                  <input type="checkbox" checked={ptoBalOverride} onChange={(e) => setPtoBalOverride(e.target.checked)} /> Approve over the available balance (admin override)
                                </label>
                                {ptoBalOverride && (
                                  <Textarea data-testid="approve-balance-override-reason" value={ptoOverrideReason} onChange={(e) => setPtoOverrideReason(e.target.value)} rows={2} placeholder="Reason for over-balance approval (required)" className="mt-2 bg-[#0A0A0A] border-[#262626] text-white" />
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </>
                    );
                  })()}
                  <p className="text-[11px] text-[#525252] mt-2">Sick leave is unlimited and never reduces PTO. Standard max PTO per day is {review.pto_max_per_day}h.</p>
                </div>
              )}

              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Paid hours per day</Label>
                <div className="mt-1 space-y-1.5">
                  {(review.dates || []).map((d) => (
                    <div key={d} className="flex items-center gap-3">
                      <span className="text-sm text-white flex-1">{dayLabel(d)}</span>
                      <Input
                        data-testid={`approve-hours-${d}`}
                        type="number" min="0" max={review.is_pto ? (review.pto_max_per_day || 8) : 24} step="0.25"
                        value={dayHours[d] ?? 0}
                        onChange={(e) => setDayHours({ ...dayHours, [d]: e.target.value })}
                        className="bg-[#0A0A0A] border-[#262626] text-white w-24 text-right"
                      />
                      <span className="text-xs text-[#A3A3A3] w-8">hrs</span>
                    </div>
                  ))}
                </div>
                <p className="text-right text-sm text-green-400 mt-2" data-testid="approve-total">Total: {totalPaid.toFixed(2)} paid hours</p>
              </div>

              <div className="flex gap-2 pt-2">
                <Button data-testid="approve-confirm" onClick={submitApproval} disabled={saving
                  || (review.is_pto && review.pto_eligible === false)
                  || (review.is_pto && review.pto_summary
                      && (Math.max(0, totalPaid - (review.pto_already_used || 0)) > review.pto_summary.current_available + 1e-9)
                      && !(ptoBalOverride && ptoOverrideReason.trim()))
                } className="flex-1 bg-green-600 hover:bg-green-700 text-white">{saving ? 'Approving…' : 'Approve & Block Schedule'}</Button>
                <Button variant="outline" onClick={() => { setReviewFor(null); setReview(null); setPtoBalOverride(false); setPtoOverrideReason(''); }} className="border-[#262626] text-[#A3A3A3]">Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectFor} onOpenChange={(o) => { if (!o) setRejectFor(null); }}>
        <DialogContent data-testid="timeoff-reject-dialog" className="bg-[#141414] border-[#262626] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Reject Time Off — {rejectFor?.user_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea data-testid="reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3} placeholder="Reason (shared with the employee)" className="bg-[#0A0A0A] border-[#262626] text-white" />
            <div className="flex gap-2">
              <Button data-testid="reject-confirm" onClick={submitReject} disabled={saving} className="flex-1 bg-red-600 hover:bg-red-700 text-white">Reject Request</Button>
              <Button variant="outline" onClick={() => setRejectFor(null)} className="border-[#262626] text-[#A3A3A3]">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* iter232 — Sync report */}
      <Dialog open={!!syncReport} onOpenChange={(o) => { if (!o) setSyncReport(null); }}>
        <DialogContent data-testid="timeoff-sync-report" className="bg-[#141414] border-[#262626] text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2"><Calendar className="w-4 h-4 text-[#D4AF37]" /> Sync Approved Time Off</DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">Idempotent backfill/repair of schedule blocks for approved requests.</DialogDescription>
          </DialogHeader>
          {syncReport && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2">
                {[
                  ['Requests scanned', syncReport.requests_scanned, 'text-white'],
                  ['Blocks created', syncReport.blocks_created, 'text-green-400'],
                  ['Already correct', syncReport.blocks_already_correct, 'text-[#A3A3A3]'],
                  ['Blocks removed', syncReport.blocks_removed, 'text-red-400'],
                  ['Conflicts found', syncReport.conflicts_found, 'text-amber-400'],
                  ['Errors', (syncReport.errors || []).length, (syncReport.errors || []).length ? 'text-red-400' : 'text-[#A3A3A3]'],
                ].map(([label, val, cls]) => (
                  <div key={label} className="bg-[#0A0A0A] border border-[#262626] rounded-md p-2 text-center">
                    <div className={`text-lg font-bold ${cls}`}>{val ?? 0}</div>
                    <div className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">{label}</div>
                  </div>
                ))}
              </div>
              {(syncReport.errors || []).length > 0 && (
                <div className="rounded-md border border-red-800/50 bg-red-950/20 p-2 text-xs text-red-200 space-y-1">
                  {syncReport.errors.map((e, i) => <div key={i}>• {e.employee || e.request_id}: {e.error}</div>)}
                </div>
              )}
              <div className="max-h-52 overflow-y-auto space-y-1">
                {(syncReport.results || []).map((r, i) => (
                  <div key={i} className="flex items-center justify-between bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1 text-xs">
                    <span className="text-white">{r.employee || `Orphan ${r.request_id?.slice(-6)}`}{r.start_date ? ` · ${r.start_date}→${r.end_date}` : ''}</span>
                    <span className={
                      r.status === 'repaired' ? 'text-green-400' :
                      r.status === 'orphan_removed' ? 'text-red-400' : 'text-[#A3A3A3]'
                    }>
                      {r.status === 'orphan_removed' ? `removed ${r.blocks_removed}` : r.status === 'repaired' ? `blocked ${r.blocks_now}d` : 'ok'}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex justify-end pt-1">
                <Button data-testid="sync-report-close" variant="outline" onClick={() => setSyncReport(null)} className="border-[#262626] text-[#A3A3A3]">Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTimeOff;
